/**
 * Settings → Audit Trail Configuration.
 *
 * The single GLOBAL configuration form (ported from ALPACA's
 * AuditTrailToggleControl / AuditTrailReasontoChangeViewController, Settings →
 * Audit Trail Configuration).  It owns, PER FORM, three settings that drive the
 * audit trail app-wide (CLAUDE.md standing rule 11 — never hardcoded per form):
 *
 *   • Audit Trail   — show the form's audit log (its <AuditTrailButton> + the
 *                     Data Center → Audit Trail Log views) or hide it.
 *   • Reason Required — prompt the user for a "reason for change" on edit/delete.
 *   • Retention Days — stored + displayed (e.g. "1Y"); NOT enforced (no purge).
 *
 * The audit-log VIEW stays in Data Center; only this CONFIGURATION lives in
 * Settings (matching ALPACA).  Storage is the shared AI_AppSettings key
 * `AuditTrailConfig` (JSON) via GET/PUT /api/v1/app-config (audited as its own
 * form "Audit Trail Configuration").  Built entirely on the shared
 * <AppDataGrid>/<AppToolbar>/<FormTitle>/useSaveFlow (standing rules 8 & 9).
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar, EditGateActions } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useNotify } from '../../components/notifications';
import { useEditGate } from '../../components/confirm';
import { setAuditTrailConfig, type AuditFormConfig } from '../../utils/auditConfig';
import { navTree, type NavGroupChild } from '../../layouts/navData';
import { useFetchMs } from '../../hooks/useFetchMs';
import '../backlog/backlog.css';

/** Retention options — mirror ALPACA's audit ranges (display-only, not enforced). */
const RETENTION_OPTIONS = ['1M', '3M', '6M', '1Y', '2Y', '5Y'] as const;

/** One auditable form in the config grid (ALPACA's DefaultSetting BO list
 *  equivalent: Module · BO/Form · Path).  `formName` is what the audit views key
 *  on (dbo.AuditData.FormName / the <AuditTrailButton formName> on each form). */
interface CatalogForm { id: string; module: string; formName: string; path: string }

/** Nav-id → audit FormName overrides — the SINGLE place to record a form whose
 *  audit FormName (dbo.AuditData.FormName) differs from its nav label.  Every
 *  other form defaults formName = nav label, so a NEW form added to the nav
 *  appears here automatically with no edit (standing rule 11 — config is derived,
 *  never hardcoded per form).  Defaulting to label is SAFE: if it doesn't match a
 *  form's real FormName the audit simply stays ON (never wrongly hidden). */
const FORMNAME_OVERRIDES: Record<string, string> = {
  morreport: 'MOR Reporting',
  morlist: 'MOR Adhoc Reporting',
  'mor-template': 'MOR Template Setup',
  custreport: 'Custom Reporting',
  custreportbuilder: 'Custom Reporting',
  formsetup: 'Form Config',
  appconfig: 'App Config',
  emailalerts: 'Email Alert Settings',
  'adc-chain': 'Analysis Department Chain',
  'acc-paymentterms-setup': 'Payment Terms Setup',
  'sm-country': 'Country', 'sm-state': 'State', 'sm-city': 'City',
  'sm-department': 'Department', 'sm-position': 'Position', 'sm-division': 'Division',
  sampreg: 'Sample Registration', fielddata: 'Field Data Entry', samptransfer: 'Sample Transfer',
};

/** Build the form catalog from the nav tree — so EVERY nav form (and any future
 *  one) automatically appears in Audit Trail Configuration, exactly like ALPACA
 *  auto-seeds its grid from the module/form registry.  Read-only dashboards are
 *  excluded; everything navigable is included (an admin can still toggle a form
 *  that happens to have no audit rows yet). */
const AUDIT_FORM_CATALOG: CatalogForm[] = (() => {
  const out: CatalogForm[] = [];
  const seen = new Set<string>();
  // Forms that are pure read-only views (no audit) — keep the grid focused.
  const EXCLUDE = new Set(['home', 'execdash', 'avainsight', 'trendanalysis']);

  const add = (module: string, id: string, label: string) => {
    if (!id || EXCLUDE.has(id) || id.startsWith('at-')) return;
    const formName = FORMNAME_OVERRIDES[id] ?? label;
    // Config is keyed by formName — dedupe so two nav entries that map to the
    // same audit FormName (e.g. Custom Reporting + Custom Report Builder) are one row.
    if (seen.has(formName)) return;
    seen.add(formName);
    out.push({ id, module, formName, path: `${module}\\${label}` });
  };
  const walk = (children: NavGroupChild[], module: string) => {
    for (const c of children) {
      if (c.kind === 'item') add(module, c.item.id, c.item.label);
      else if (c.kind === 'subgroup') walk(c.subgroup.children, module);
    }
  };

  let section = 'Overview';
  for (const e of navTree) {
    if (e.kind === 'section') section = e.label;
    else if (e.kind === 'item') add(section, e.item.id, e.item.label);
    else if (e.kind === 'group') {
      for (const it of e.group.items) add(e.group.label, it.id, it.label);
      if (e.group.children) walk(e.group.children, e.group.label);
    }
  }
  // Sort by module then form for a stable, scannable grid.
  return out.sort((a, b) => a.module.localeCompare(b.module) || a.formName.localeCompare(b.formName));
})();

/** Per-form working state (defaults: audit ON, reason OFF, retention "1Y"). */
interface Working { audit: boolean; reason: boolean; retention: string }
type WorkingMap = Record<string, Working>;

const DEFAULT_ROW: Working = { audit: true, reason: false, retention: '1Y' };

/** Overlay the saved server config (formName → cfg) onto the full catalog so a
 *  form with no saved row falls back to the defaults. */
function buildWorking(saved: Record<string, AuditFormConfig>): WorkingMap {
  const byName: Record<string, AuditFormConfig> = {};
  for (const k of Object.keys(saved)) {
    const v = saved[k];
    if (v) byName[(v.formName ?? k).trim().toLowerCase()] = v;
  }
  const out: WorkingMap = {};
  for (const f of AUDIT_FORM_CATALOG) {
    const s = byName[f.formName.trim().toLowerCase()];
    out[f.formName] = s
      ? { audit: s.auditEnabled !== false, reason: s.reasonRequired === true, retention: s.retentionDays || '1Y' }
      : { ...DEFAULT_ROW };
  }
  return out;
}

interface GridRow extends CatalogForm { audit: boolean; reason: boolean; retention: string }

export default function AuditTrailConfigurationPage() {
  // View-then-Edit gate (BO UI Standard v2) — the SHARED config-panel flow. The
  // page opens READ-ONLY; the toolbar Edit button unlocks the grid controls;
  // Save persists the whole config in one audited batch (shared useSaveFlow);
  // Cancel reverts to the last-loaded server snapshot.
  const notify = useNotify();
  const gate = useEditGate();
  const ro = gate.readOnly;
  const [working, setWorking] = useState<WorkingMap>(() => buildWorking({}));
  const [snapshot, setSnapshot] = useState<WorkingMap>(() => buildWorking({}));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const loadMs = useFetchMs(loading);
  const [error, setError] = useState<string | null>(null);

  const load = useMemo(() => async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/api/v1/app-config');
      const cfg = (res.data as { data?: { auditTrailConfig?: Record<string, AuditFormConfig> } })
        ?.data?.auditTrailConfig ?? {};
      const built = buildWorking(cfg);
      setWorking(built);
      setSnapshot(built);
      setAuditTrailConfig(cfg);   // keep the global store fresh
    } catch {
      setError('Could not load audit trail configuration.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const setRow = (formName: string, patch: Partial<Working>) =>
    setWorking(w => ({ ...w, [formName]: { ...(w[formName] ?? DEFAULT_ROW), ...patch } }));

  const rows: GridRow[] = useMemo(
    () => AUDIT_FORM_CATALOG.map(f => ({ ...f, ...(working[f.formName] ?? DEFAULT_ROW) })),
    [working],
  );

  /** Persist the whole per-form config in one audited batch (the shared Save). */
  const persistAll = async () => {
    const entries = AUDIT_FORM_CATALOG.map(f => {
      const w = working[f.formName] ?? DEFAULT_ROW;
      return {
        formName: f.formName,
        auditEnabled: w.audit,
        reasonRequired: w.reason,
        retentionDays: w.retention,
      };
    });
    const res = await api.put('/api/v1/app-config/audit-trail-config', { entries });
    const saved = (res.data as { data?: { auditTrailConfig?: Record<string, AuditFormConfig> } })
      ?.data?.auditTrailConfig ?? {};
    const built = buildWorking(saved);
    setWorking(built);
    setSnapshot(built);
    setAuditTrailConfig(saved);   // drives <AuditTrailButton> + reason prompts app-wide
  };

  /** Toolbar Save — the shared view-then-edit save (confirm → audited batch →
   *  "Saved successfully." → back to read-only). */
  const handleSave = () => {
    setSaving(true);
    void gate.save({
      formName: 'Audit Trail Configuration',
      successMessage: 'Audit trail configuration saved successfully.',
      save: persistAll,
      reload: load,
      onError: (e: unknown) => {
        const status = (e as { response?: { status?: number } })?.response?.status;
        notify.error(status === 403
          ? 'Administrator / service access required to change audit trail configuration.'
          : 'Save failed. Please try again.');
      },
    }).finally(() => setSaving(false));
  };

  /** Cancel — discard edits by reverting to the last-loaded server snapshot. */
  const handleCancel = () => gate.cancel(() => setWorking(snapshot));

  /** Centered checkbox cell — stops row-click propagation so toggling never
   *  triggers grid row selection. */
  const checkCell = (checked: boolean, onToggle: () => void) => (
    <span onClick={e => e.stopPropagation()} style={{ display: 'flex', justifyContent: 'center' }}>
      <input type="checkbox" checked={checked} onChange={onToggle} disabled={ro} />
    </span>
  );

  const columns: AppColumn<GridRow>[] = [
    { field: 'module',   header: 'Module', width: 150, text: r => r.module },
    { field: 'formName', header: 'Form',   width: 200, text: r => r.formName },
    { field: 'path',     header: 'Path',   text: r => r.path },
    { field: 'audit', header: 'Audit Trail', width: 110, align: 'center', sortable: false, filterable: false,
      text: r => (r.audit ? 'Yes' : 'No'),
      render: r => checkCell(r.audit, () => setRow(r.formName, { audit: !r.audit })) },
    { field: 'reason', header: 'Reason Required', width: 130, align: 'center', sortable: false, filterable: false,
      text: r => (r.reason ? 'Yes' : 'No'),
      render: r => checkCell(r.reason, () => setRow(r.formName, { reason: !r.reason })) },
    { field: 'retention', header: 'Retention Days', width: 130, align: 'center', sortable: false, filterable: false,
      text: r => r.retention,
      render: r => (
        <span onClick={e => e.stopPropagation()} style={{ display: 'flex', justifyContent: 'center' }}>
          <select value={r.retention} disabled={ro} onChange={e => setRow(r.formName, { retention: e.target.value })}>
            {RETENTION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </span>
      ) },
  ];

  return (
    <div className="bl-page">
      <div className="bl-grid-area">
        <div className="bl-card bl-card-flex">
          <div className="section-hdr" style={{ flexWrap: 'wrap', gap: 10 }}>
            <FormTitle pageId="audittrailcfg" loadMs={loadMs} loading={loading} />
            <AppToolbar
              left={
                <EditGateActions
                  gate={gate}
                  saving={saving}
                  disabled={loading}
                  onSave={handleSave}
                  onCancel={handleCancel}
                />
              }
              onRefresh={() => void load()}
              refreshing={loading}
              refreshDisabled={gate.editing}
            />
          </div>

          {error && (
            <div style={{ margin: '10px 20px 0', padding: '8px 12px', borderRadius: 6,
              background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: 13 }}>
              <i className="ti ti-alert-circle" style={{ marginRight: 6, verticalAlign: 'middle' }} />{error}
            </div>
          )}

          <AppDataGrid<GridRow>
            rows={rows}
            columns={columns}
            getRowId={r => r.formName}
            loading={loading}
            emptyText="No auditable forms configured."
          />
        </div>
      </div>
    </div>
  );
}
